import { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { compose } from "redux";

import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { fetchContactsFromActionsPanelThunk } from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/effects";
import LeadGenRevealActionButtonContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/RevealActionButton/LeadGenRevealActionButtonContainer";
import {
    selectActionsPanelFilters,
    selectIsPanelExportActive,
    selectMultipleRevealIsInProgress,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { withIntegrationsSyncCall } from "app/pages/sales-intelligence/sub-modules/management/hocs/integration-sync/withIntegrationsSyncCall";
import withDynamicsStatusCall from "app/pages/sales-intelligence/sub-modules/management/hocs/manage-status/withDynamicsStatusCall";
import withHubspotStatusCall from "app/pages/sales-intelligence/sub-modules/management/hocs/manage-status/withHubspotStatusCall";
import withSalesforceStatusCall from "app/pages/sales-intelligence/sub-modules/management/hocs/manage-status/withSalesforceStatusCall";
import withMappingFieldsCall from "app/pages/sales-intelligence/sub-modules/management/hocs/withMappingFieldsCall";

import ActivatedExportPanelContainer from "../ActivatedExportPanel/ActivatedExportPanelContainer";
import ActiveContactsRevealPanelContainer from "../ActiveContactsRevealPanel/ActiveContactsRevealPanelContainer";
import ExportVariantsDropdownContainer from "../ExportVariantsDropdownContainer/ExportVariantsDropdownContainer";

import { StyledActionsPanel } from "./styles";

type TableActionsPanelProps = {
    onApprove?: () => Promise<unknown> | undefined;
};

const TableActionsPanelContainer = ({ onApprove }: TableActionsPanelProps) => {
    const dispatch = useDispatch();
    const actionsPanelFilters = useSelector(selectActionsPanelFilters);
    const isExportFlowActive = useSelector(selectIsPanelExportActive);
    const isMultipleRevealInProgress = useSelector(selectMultipleRevealIsInProgress);
    const { params } = useCurrentStateAndParamsWithFixedDomain();

    const defaultActiveChild = useMemo(() => {
        if (isExportFlowActive) {
            return ActivatedExportPanelContainer;
        }
        if (isMultipleRevealInProgress) {
            return ActiveContactsRevealPanelContainer;
        }
        return undefined;
    }, [isExportFlowActive, isMultipleRevealInProgress]);

    useDidUpdate(() => {
        const ac = new AbortController();

        void dispatch(
            fetchContactsFromActionsPanelThunk(params.key, actionsPanelFilters, {
                cancellation: ac.signal,
            }),
        );

        return () => {
            ac.abort();
        };
    }, [
        actionsPanelFilters.excludeExported,
        actionsPanelFilters.includeOnlyExported,
        actionsPanelFilters.includeOnlyNotRevealed,
        actionsPanelFilters.includeOnlyRevealed,
    ]);

    return (
        <StyledActionsPanel defaultActiveChild={defaultActiveChild}>
            {({ activate }) => {
                function activateRevealPanel() {
                    activate(() => ActiveContactsRevealPanelContainer);
                }

                return (
                    <>
                        <LeadGenRevealActionButtonContainer
                            onClick={activateRevealPanel}
                            onApprove={onApprove}
                        />
                        <ExportVariantsDropdownContainer
                            isDisabled={false}
                            onActivate={activate}
                        />
                    </>
                );
            }}
        </StyledActionsPanel>
    );
};

export default compose(
    withSalesforceStatusCall,
    withHubspotStatusCall,
    withDynamicsStatusCall,
    withMappingFieldsCall,
    withIntegrationsSyncCall,
)(TableActionsPanelContainer);
